import React, { useState, useEffect } from 'react';
import { getFunctions, httpsCallable } from 'firebase/functions';
import { onAuthStateChanged } from 'firebase/auth';
import { auth } from '../../config/firebase-config';
import { Trophy, TrendingUp, Star, Clock, Users, Award, BarChart3 } from 'lucide-react';

const MusicianGamification = () => {
  const [user, setUser] = useState(null);
  const [gamificationData, setGamificationData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const functions = getFunctions();

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, (currentUser) => {
      setUser(currentUser);
      if (currentUser) {
        loadGamificationData(currentUser.uid);
      } else {
        setLoading(false);
      }
    });

    return () => unsubscribe();
  }, []);

  const loadGamificationData = async (userId) => {
    try {
      setLoading(true);
      setError(null);

      const getMusicianStats = httpsCallable(functions, 'getMusicianStats');
      const result = await getMusicianStats({ musicianId: userId });

      if (result.data.success) {
        setGamificationData(result.data.data);
      } else {
        // Handle feature not activated gracefully
        if (result.data.message && result.data.message.includes('not yet activated')) {
          setError('Gamification features are coming soon!');
        } else {
          setError(result.data.message || 'Failed to load gamification data');
        }
      }
    } catch (err) {
      // Handle CORS or network errors gracefully
      console.error('Error loading gamification data:', err);
      if (err.message?.includes('CORS') || err.message?.includes('fetch')) {
        setError('Gamification features are being updated. Please check back later.');
      } else {
        setError(err.message || 'An error occurred while loading gamification data');
      }
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-900 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-purple-500 mx-auto mb-4"></div>
          <p className="text-gray-400">Loading your stats...</p>
        </div>
      </div>
    );
  }

  if (!user) {
    return (
      <div className="min-h-screen bg-gray-900 flex items-center justify-center">
        <div className="text-center">
          <div className="text-red-500 text-xl mb-4">🔒 Access Denied</div>
          <p className="text-gray-400">Please log in to view your gamification stats.</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gray-900 flex items-center justify-center">
        <div className="text-center">
          <div className="text-red-500 text-xl mb-4">⚠️ Error</div>
          <p className="text-gray-400">{error}</p>
          <button 
            onClick={() => loadGamificationData(user.uid)}
            className="mt-4 bg-purple-600 hover:bg-purple-700 text-white px-4 py-2 rounded-lg"
          >
            Try Again
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-900 text-white p-6">
      <div className="max-w-6xl mx-auto">
        <div className="mb-8">
          <h1 className="text-3xl font-bold mb-2">🎮 Your Musician Journey</h1>
          <p className="text-gray-400">Track your progress, earn badges, and climb the leaderboards!</p>
        </div>
        
        <div className="bg-gray-800 rounded-xl p-6 mb-6">
          <h2 className="text-xl font-bold mb-4 flex items-center gap-2">
            <Trophy className="text-yellow-500" />
            Gamification Dashboard
          </h2>
          <p className="text-gray-400 mb-4">
            Welcome to your musician gamification hub! Here you'll be able to:
          </p>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            <div className="bg-gray-700 p-4 rounded-lg">
              <div className="flex items-center gap-2 mb-2">
                <Star className="text-yellow-500" size={20} />
                <h3 className="font-semibold">Track XP & Levels</h3>
              </div>
              <p className="text-sm text-gray-400">Earn experience points for completing requests and level up!</p>
            </div>
            <div className="bg-gray-700 p-4 rounded-lg">
              <div className="flex items-center gap-2 mb-2">
                <Award className="text-purple-500" size={20} />
                <h3 className="font-semibold">Unlock Badges</h3>
              </div>
              <p className="text-sm text-gray-400">Collect badges for achievements and milestones.</p>
            </div>
            <div className="bg-gray-700 p-4 rounded-lg">
              <div className="flex items-center gap-2 mb-2">
                <Users className="text-blue-500" size={20} />
                <h3 className="font-semibold">Leaderboards</h3>
              </div>
              <p className="text-sm text-gray-400">Compete with other musicians and climb the rankings!</p>
            </div>
          </div>
        </div>

        <div className="bg-gray-800 rounded-xl p-6">
          <h3 className="text-lg font-bold mb-4 flex items-center gap-2">
            <BarChart3 className="text-green-500" />
            Coming Soon
          </h3>
          <p className="text-gray-400">
            Full gamification features are being developed and will be available soon. 
            This includes detailed stats, achievement tracking, and competitive leaderboards.
          </p>
          <div className="mt-4 p-4 bg-gray-700 rounded-lg">
            <p className="text-sm text-gray-300">
              <strong>Note:</strong> The gamification system requires backend Cloud Functions to be deployed. 
              Once the <code className="bg-gray-600 px-2 py-1 rounded">getMusicianStats</code> function is available, 
              this dashboard will display your live stats and achievements.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default MusicianGamification;